import { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader, EmptyState } from "../components/ui";
import { listWipBalances } from "../api/wip";
import { listDimensionOptions, listVariantDimensions } from "../api/variantDimensions";

const FILTER_KEYS = ["baskili_mi", "renk", "sekil", "cap", "gramaj", "paket_adedi"];

// Parti (lot) numarasındaki rezervasyon eki (ör. /R55) ekranda gösterilmez.
function displayLotNo(lotNo) {
  const rawLotNo = String(lotNo || "").trim();
  return rawLotNo.replace(/\s*\/R\d+/i, "").trim();
}

function buildRawMaterialSummary(rawMaterials) {
  const rms = Array.isArray(rawMaterials) ? rawMaterials : [];
  const paper = rms.find((x) => x?.raw_material_type === "paper" && x?.paper_specs) || null;
  if (paper?.paper_specs) {
    const s = paper.paper_specs;
    const bits = [
      s.paper_kind_code && `Cins:${s.paper_kind_code}`,
      s.paper_width_code && `En:${s.paper_width_code}`,
      s.paper_length_code && `Boy:${s.paper_length_code}`,
      s.paper_grammage_code && `Gr:${s.paper_grammage_code}`,
      s.paper_form && `Form:${s.paper_form}`,
    ].filter(Boolean);
    if (bits.length) return `Kağıt: ${bits.join(" ")}`;
  }
  return rms.length ? `Hammadde: ${rms.map((x) => x?.material_code).filter(Boolean).join(", ")}` : "";
}

export default function WipBalances() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productionOrderId, setProductionOrderId] = useState("");

  const [dims, setDims] = useState([]);
  const [optionsByKey, setOptionsByKey] = useState({});
  const [filters, setFilters] = useState(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));

  async function load() {
    setLoading(true);
    try {
      const data = await listWipBalances({
        production_order_id: productionOrderId ? Number(productionOrderId) : undefined,
      });
      setItems(data || []);
    } catch (e) {
      console.error("WIP LOAD ERROR", e);
      alert("Ara stok (WIP) yüklenemedi");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const all = await listVariantDimensions();
        if (cancelled) return;
        const filtered = (all || []).filter((d) => FILTER_KEYS.includes(String(d.key)));
        setDims(filtered);

        const entries = await Promise.all(
          filtered.map(async (d) => [String(d.key), await listDimensionOptions(Number(d.id))])
        );
        if (cancelled) return;
        const next = {};
        for (const [k, opts] of entries) next[k] = opts || [];
        setOptionsByKey(next);
      } catch (e) {
        console.error("WIP FILTERS LOAD ERROR", e);
      }
    }
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v);
    if (!active.length) return items;
    return (items || []).filter((it) => {
      const attrs = it?.variant_attributes || null;
      for (const [k, v] of active) {
        const wanted = Number(v);
        const got = attrs?.[k]?.option_id === null || attrs?.[k]?.option_id === undefined ? null : Number(attrs[k].option_id);
        if (!Number.isFinite(wanted) || !Number.isFinite(got) || wanted !== got) return false;
      }
      return true;
    });
  }, [items, filters]);

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Ara Stok (WIP)"
        subtitle="Operasyon çıktısı lotları ve anlık bakiyeleri."
        actions={
          <Button variant="contained" onClick={load} disabled={loading}>
            Yenile
          </Button>
        }
      />

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Filtreler
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              label="İş Emri ID (opsiyonel)"
              value={productionOrderId}
              onChange={(e) => setProductionOrderId(e.target.value)}
            />
          </Grid>

          {dims.map((d) => (
            <Grid item xs={12} md={3} key={d.key}>
              <FormControl size="small" fullWidth>
                <InputLabel id={`wip-filter-${d.key}`}>{d.label}</InputLabel>
                <Select
                  labelId={`wip-filter-${d.key}`}
                  label={d.label}
                  value={filters[d.key] || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, [d.key]: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>Tümü</em>
                  </MenuItem>
                  {(optionsByKey[d.key] || []).map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}

          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setFilters(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])))}
              fullWidth
            >
              Filtreleri Temizle
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Bakiye
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredItems.length} kayıt
          </Typography>
        </Stack>

        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {!filteredItems.length ? (
          <EmptyState title="Kayıt yok" description="Seçilen filtrelere göre WIP bulunamadı." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Malzeme</TableCell>
                <TableCell>Varyant</TableCell>
                <TableCell>Lot</TableCell>
                <TableCell align="right">Miktar</TableCell>
                <TableCell>İş Emri</TableCell>
                <TableCell>Operasyon</TableCell>
                <TableCell>Hammadde</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((x) => (
                <TableRow
                  key={`${x.material_id}:${x.material_variant_id || 0}:${x.lot_no}`}
                  hover
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 800 }}>
                      {x.material_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {x.material_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{x.variant_display_name || ""}</TableCell>
                  <TableCell>{displayLotNo(x.lot_no)}</TableCell>
                  <TableCell align="right">
                    {Number(x.on_hand_qty || 0).toFixed(4)} {x.material_unit || ""}
                  </TableCell>
                  <TableCell>
                    {x.ref?.production_order_no || ""}
                    <Typography variant="body2" color="text.secondary">
                      ID: {x.ref?.production_order_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {x.ref?.routing_operation?.op_no} - {x.ref?.routing_operation?.operation_code}
                    <Typography variant="body2" color="text.secondary">
                      {x.ref?.routing_operation?.description || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{buildRawMaterialSummary(x.raw_materials)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

